import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { plural } from '../lib/format'
import {
  KIND_TITLE,
  ticketsApi,
  type CategoryAvailability,
  type TemplateCheck,
  type TicketCategory,
  type TicketKind,
} from './api'

/**
 * Новое обращение.
 *
 * Вид выбирается первым, и от него зависит всё остальное: разовому нужна
 * программа и направление, аренде — база из ваших доступов, кодам — товарная
 * группа и заполненный шаблон. Спрашиваем ровно то, что нужно выбранному виду:
 * общая форма «на все случаи» заставляла бы каждого пропускать чужие поля.
 *
 * Недоступные виды показываем с причиной, а не прячем. Спрятанный пункт человек
 * ищет и звонит спросить, куда он делся.
 */
export default function NewTicketDialog({
  kind,
  onClose,
  onOpen,
}: {
  kind: TicketKind
  onClose: () => void
  onOpen: (id: number) => void
}) {
  const qc = useQueryClient()
  const form = useQuery({
    queryKey: ['portal', 'ticket-form', kind],
    queryFn: () => ticketsApi.form(kind),
  })

  const [category, setCategory] = useState<TicketCategory | null>(null)
  const один = KIND_TITLE[kind].toLowerCase()
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [program, setProgram] = useState('')
  const [direction, setDirection] = useState('')
  const [accessId, setAccessId] = useState<number | null>(null)
  const [templateKey, setTemplateKey] = useState('')
  const [check, setCheck] = useState<TemplateCheck | null>(null)
  const [error, setError] = useState('')

  const create = useMutation({
    mutationFn: () =>
      ticketsApi.create({
        subject: subject.trim(),
        description: description.trim() || null,
        category: category!,
        program: program || null,
        direction: direction || null,
        access_id: accessId,
        code_template: category === 'codes' ? templateKey || null : null,
        upload_id: check?.upload_id ?? null,
      }),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['portal', 'tickets'] })
      onClose()
      onOpen(t.id)
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось отправить'),
  })

  const data = form.data
  const chosen = data?.categories.find((c) => c.category === category) ?? null

  // Сменили вид — поля прошлого вида не должны уехать в заявку
  function pick(c: CategoryAvailability) {
    if (!c.available) return
    setCategory(c.category)
    setProgram('')
    setDirection('')
    setAccessId(null)
    setTemplateKey('')
    setCheck(null)
    setError('')
  }

  const ready =
    !!category &&
    subject.trim().length > 0 &&
    (category !== 'work' || (!!program && !!direction)) &&
    (category !== 'rent_1c' || (!!accessId && !!direction)) &&
    (category !== 'codes' || (!!templateKey && !!check && check.ok))

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        className="modal wide"
        style={{ maxWidth: 640 }}
        onSubmit={(e) => {
          e.preventDefault()
          setError('')
          create.mutate()
        }}
      >
        <h2>{kind === 'order' ? 'Новая заявка' : 'Новое обращение'}</h2>
        <div className="modal-body">
          {error && <div className="error">{error}</div>}
          {form.isLoading && <p className="empty">Загрузка…</p>}
          {form.isError && (
            <div className="error">Не удалось загрузить форму. Обновите страницу.</div>
          )}

          {data && (
            <>
              <div className="field full">
                <label>
                  {kind === 'order' ? 'Что нужно сделать' : 'С чем обращаетесь'}
                </label>
                <div className="tk-cats">
                  {data.categories.map((c) => (
                    <button
                      key={c.category}
                      type="button"
                      className={`tk-cat${category === c.category ? ' active' : ''}${
                        c.available ? '' : ' disabled'
                      }`}
                      onClick={() => pick(c)}
                      title={c.reason ?? undefined}
                    >
                      {c.title}
                    </button>
                  ))}
                </div>
                {chosen && !chosen.available && chosen.reason && (
                  <div className="warn" style={{ marginTop: 8 }}>
                    {chosen.reason}
                  </div>
                )}
                {!category && (
                  <span className="hint-inline">
                    Недоступные виды подписаны — наведите, чтобы увидеть причину
                  </span>
                )}
              </div>

              {category === 'work' && (
                <>
                  <Choice
                    id="nt-prog"
                    label="Программа"
                    value={program}
                    options={data.work_programs}
                    onChange={setProgram}
                  />
                  <Choice
                    id="nt-dir"
                    label="Направление"
                    value={direction}
                    options={data.work_directions}
                    onChange={setDirection}
                  />
                </>
              )}

              {category === 'rent_1c' && (
                <>
                  <div className="field">
                    <label htmlFor="nt-base">База 1С</label>
                    <select
                      id="nt-base"
                      value={accessId ?? ''}
                      onChange={(e) => setAccessId(Number(e.target.value) || null)}
                      required
                    >
                      <option value="">— выберите —</option>
                      {data.rent_programs.map((p) => (
                        <option key={p.access_id} value={p.access_id}>
                          {p.title}
                        </option>
                      ))}
                    </select>
                    <span className="hint-inline">
                      Список — из ваших доступов. Нужной базы нет? Напишите менеджеру.
                    </span>
                  </div>
                  <Choice
                    id="nt-rdir"
                    label="Что нужно"
                    value={direction}
                    options={data.rent_directions}
                    onChange={setDirection}
                  />
                </>
              )}

              {category === 'codes' && (
                <CodesFields
                  groups={data.code_templates}
                  templateKey={templateKey}
                  onTemplate={(key) => {
                    setTemplateKey(key)
                    setCheck(null)
                  }}
                  check={check}
                  onChecked={setCheck}
                  onError={setError}
                />
              )}

              {category && (
                <>
                  <div className="field full">
                    <label htmlFor="nt-subj">Тема</label>
                    <input
                      id="nt-subj"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder={PLACEHOLDER[category]}
                      required
                    />
                  </div>
                  <div className="field full">
                    <label htmlFor="nt-desc">Что нужно сделать</label>
                    <textarea
                      id="nt-desc"
                      rows={4}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Опишите подробнее: что делали, что увидели, с какого времени"
                    />
                    <span className="hint-inline">
                      Чем точнее опишете, тем меньше уточняющих вопросов. Файлы можно
                      приложить сразу после отправки — в самом обращении.
                    </span>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button className="btn primary" disabled={create.isPending || !ready}>
            {create.isPending ? 'Отправляем…' : `Отправить ${один}`}
          </button>
        </div>
      </form>
    </div>
  )
}

const PLACEHOLDER: Record<TicketCategory, string> = {
  support: 'Вопрос по начислению отпускных',
  rent_1c: 'Добавить кладовщика в базу',
  codes: 'Коды на весеннюю коллекцию',
  work: 'Настроить онлайн-кассу',
}

function Choice({
  id,
  label,
  value,
  options,
  onChange,
}: {
  id: string
  label: string
  value: string
  options: string[]
  onChange: (v: string) => void
}) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} required>
        <option value="">— выберите —</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  )
}

/**
 * Коды: товарная группа, скачивание шаблона и его загрузка обратно.
 *
 * Количество не спрашиваем — считаем по файлу. Человек пишет «двести», а в
 * файле оказывается сто восемьдесят три, и расхождение всплывает уже у
 * оператора. Что насчитали, показываем до отправки: заявка уйдёт с этим числом.
 */
function CodesFields({
  groups,
  templateKey,
  onTemplate,
  check,
  onChecked,
  onError,
}: {
  groups: { group: string; items: { key: string; title: string }[] }[]
  templateKey: string
  onTemplate: (key: string) => void
  check: TemplateCheck | null
  onChecked: (c: TemplateCheck | null) => void
  onError: (e: string) => void
}) {
  const [busy, setBusy] = useState(false)
  const [downloading, setDownloading] = useState(false)

  async function download() {
    setDownloading(true)
    onError('')
    try {
      const title =
        groups.flatMap((g) => g.items).find((t) => t.key === templateKey)?.title ?? 'ГС1'
      await ticketsApi.downloadTemplate(templateKey, `Шаблон ГС1 — ${title}.xlsx`)
    } catch (e: any) {
      onError(e?.message ?? 'Не удалось скачать шаблон')
    } finally {
      setDownloading(false)
    }
  }

  async function upload(file: File | undefined) {
    if (!file || !templateKey) return
    setBusy(true)
    onError('')
    try {
      onChecked(await ticketsApi.checkTemplate(templateKey, file))
    } catch (e: any) {
      onChecked(null)
      onError(e?.message ?? 'Не удалось прочитать файл')
    } finally {
      setBusy(false)
    }
  }

  // Списки читаем через запасное значение: сервер может оказаться старее
  // страницы и не отдать поле, которое она ждёт. Это не повод падать.
  const problems = check?.problems ?? []
  const notes = check?.notes ?? []

  return (
    <>
      <div className="field full">
        <label htmlFor="nt-group">Товарная группа</label>
        <select
          id="nt-group"
          value={templateKey}
          onChange={(e) => onTemplate(e.target.value)}
          required
        >
          <option value="">— выберите —</option>
          {groups.map((g) => (
            <optgroup key={g.group} label={g.group}>
              {g.items.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.title}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {templateKey && (
        <div className="field full field-accent">
          <label>Шаблон ГС1</label>
          <p className="hint" style={{ margin: '4px 0 10px' }}>
            Скачайте шаблон, заполните его с седьмой строки и загрузите обратно.
            Обязательны наименование товара и «Кол-во» в крайней правой колонке —
            сумму этой колонки мы и закажем. Остальные поля можно дозаполнить
            позже.
          </p>
          <button
            type="button"
            className="btn"
            disabled={downloading}
            onClick={() => void download()}
          >
            {downloading ? 'Готовим файл…' : '↓ Скачать шаблон'}
          </button>{' '}
          <label className="btn" style={{ display: 'inline-block', cursor: 'pointer' }}>
            {busy ? 'Читаем файл…' : check ? 'Заменить файл' : '↑ Загрузить заполненный'}
            <input
              type="file"
              accept=".xlsx"
              style={{ display: 'none' }}
              disabled={busy}
              onChange={(e) => void upload(e.target.files?.[0])}
            />
          </label>

          {check && (
            <div style={{ marginTop: 12 }}>
              <div className={check.ok ? 'tk-count' : 'warn'}>
                {check.ok ? (
                  <>
                    Будет заказано <b>{check.codes_count}</b>{' '}
                    {plural(check.codes_count, 'код', 'кода', 'кодов')} по{' '}
                    {check.rows} {plural(check.rows, 'строке', 'строкам', 'строкам')}{' '}
                    файла «{check.file_name}»
                  </>
                ) : (
                  <>
                    В файле {check.rows}{' '}
                    {plural(check.rows, 'строка', 'строки', 'строк')}. По этим заказ
                    не собрать — нет наименования товара или количества:
                  </>
                )}
              </div>
              {problems.length > 0 && (
                <ul className="tk-problems">
                  {problems.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              )}

              {/* Не мешает отправке: эти поля ГС1 спросит, но заявку мы
                  принимаем — их добирают по ходу. */}
              {notes.length > 0 && (
                <div className="tk-notes">
                  <div className="tk-notes-head">Пригодится заполнить — ГС1 их спросит:</div>
                  <ul>
                    {notes.map((n) => (
                      <li key={n}>{n}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  )
}
